import { ChangeDetectionStrategy, Component, Injectable, inject } from '@angular/core';
import { BrnDialogDescription, BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmBadge } from '@lite-workspace/shared/helm-ui/badge';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, HlmDialogOpener, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { hlm } from '@lite-workspace/shared/helm-ui/utils';
import { TG_SUPPORT_LINK } from '@lite-workspace/shared/directives/go-to-support';
import { SupportChannelMenuService, isPlainLeftClick } from '@lite-workspace/shared/onboarding-service';

/** Продукты, которые пока в бете и встречают фотографа этим окном. */
export type BetaProduct = 'gallery' | 'photobook';

const COPY: Record<BetaProduct, { title: string; body: string }> = {
  gallery: {
    title: 'Галереи пока в бета-версии',
    body: 'Пока идёт бета, галереями можно пользоваться бесплатно: каждая хранится 4 месяца с создания.',
  },
  photobook: {
    title: 'Фотокниги пока в бета-версии',
    body: 'Пока идёт бета, фотокнигами можно пользоваться бесплатно: каждая хранится 3 месяца с создания.',
  },
};

export interface BetaWelcomeContext {
  product: BetaProduct;
  /** Picture strip above the text; absent = text only. */
  illustration?: string;
}

// Version bump = the window shows once more to everyone who closed an older copy.
export function betaWelcomeSeenKey(product: BetaProduct): string {
  return `lumir.beta-welcome.${product}.v2`;
}

@Component({
  selector: 'gallery-beta-welcome-dialog',
  imports: [HlmBadge, HlmButton, DialogSheetComponent, BrnDialogTitle, BrnDialogDescription],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        @if (illustration) {
          <div class="h-75 overflow-hidden bg-panel2">
            <img
              [src]="illustration"
              alt=""
              width="620"
              height="300"
              decoding="async"
              class="size-full object-cover object-top"
            />
          </div>
        }
        <div [class]="bodyClass">
          <span hlmBadge variant="acc-outline">beta</span>
          <h2 brnDialogTitle class="mt-3.5 text-2xl leading-[var(--leading-title)] font-bold tracking-[var(--ls-title)] text-ink">
            {{ copy.title }}
          </h2>
          <p brnDialogDescription class="mt-3.5 text-base leading-[var(--leading-prose)] text-ink">{{ copy.body }}</p>
          <!-- На 320px две кнопки в ряд шире окна: «Понятно» переносится строкой ниже, а не вылезает за край. -->
          <div class="mt-7.5 flex flex-wrap items-center justify-between gap-4">
            <a
              hlmBtn
              variant="link"
              size="lg"
              class="px-0"
              [href]="supportLink"
              target="_blank"
              rel="noopener"
              (click)="openSupport($event)"
            >
              Поддержка в Telegram
            </a>
            <!-- Фокус при открытии — на «Понятно», не на первую ссылку: Enter закрывает окно, а не уводит в Telegram. -->
            <button hlmBtn type="button" size="lg" cdkFocusInitial (click)="close()">Понятно</button>
          </div>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class BetaWelcomeDialogComponent {
  private readonly ref = inject(BrnDialogRef);
  private readonly ctx = injectBrnDialogContext<BetaWelcomeContext>();
  private readonly support = inject(SupportChannelMenuService);
  protected readonly copy = COPY[this.ctx.product];
  protected readonly illustration = this.ctx.illustration;
  protected readonly supportLink = TG_SUPPORT_LINK;
  // Only the illustrated card is wide: the picture runs edge to edge, so the card drops its padding
  // (the text pane carries it) and clips the corners. Without a picture the card keeps its own padding.
  protected readonly hostClass = this.illustration
    ? hlm(dialogSheetHostClass('xl', 'p-9'), 'dlg-desktop:p-0 dlg-desktop:overflow-hidden')
    : dialogSheetHostClass('s', 'p-9');

  protected readonly bodyClass = this.illustration ? 'dlg-desktop:p-9 dlg-desktop:pt-8' : '';

  /** `href` stays for a middle click; a plain click opens the channel choice instead. */
  protected openSupport(event: MouseEvent): void {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    this.support.open();
  }

  protected close(): void {
    this.ref.close();
  }
}

/**
 * Окно беты показывается один раз на браузер: отметка ставится, когда фотограф его закрыл, — перезагрузка
 * с открытым окном покажет его снова. Без доступа к хранилищу окна нет совсем: лучше промолчать, чем
 * встречать им на каждом входе.
 */
@Injectable({ providedIn: 'root' })
export class BetaWelcome {
  private readonly dialogs = inject(HlmDialogOpener);

  showOnce(product: BetaProduct, illustration?: string): void {
    const key = betaWelcomeSeenKey(product);
    try {
      if (localStorage.getItem(key) !== null) return;
    } catch {
      return;
    }

    const context = { product, illustration };
    this.dialogs.open<BetaWelcomeContext, void>(BetaWelcomeDialogComponent, undefined, context).closed$.subscribe(() => {
      try {
        localStorage.setItem(key, '1');
      } catch {
        // Не записали — окно покажется ещё раз при следующем входе.
      }
    });
  }
}
